import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "../components/Logo";
import { ArrowLeft } from "lucide-react";

export default function Privacy() {
  return (
    <div className="min-h-screen" data-testid="privacy-page">
      <header className="border-b-2" style={{ borderColor: "var(--cs-ink)" }}>
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" data-testid="privacy-home"><Logo size={26} /></Link>
          <Link to="/" className="brut-btn brut-btn-ghost text-xs"><ArrowLeft size={14} /> Back</Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-12">
        <h1 className="font-display text-5xl font-black tracking-tighter">Privacy Policy</h1>
        <p className="text-[var(--cs-ink-2)] mt-2 font-semibold">Last updated: {new Date().toLocaleDateString()}</p>

        <Section title="Who we are">
          <p>CashStreak ("we", "us") is a local micro-task marketplace operated at https://mycashstreak.com. We connect people who need quick tasks done with people in the same city willing to do them for cash.</p>
        </Section>

        <Section title="Information we collect">
          <p>When you create an account or use CashStreak, we collect:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li><strong>Account info:</strong> your name, email address, password (encrypted with bcrypt), and city.</li>
            <li><strong>Task content:</strong> tasks you post or claim, including descriptions, prices, and messages with other users.</li>
            <li><strong>Financial info:</strong> payment data is processed by <a className="underline" href="https://stripe.com/privacy" target="_blank" rel="noreferrer">Stripe</a>. We do not store full credit card numbers. For taskers receiving payouts, Stripe Connect collects bank account and government ID for verification (subject to Stripe's privacy policy).</li>
            <li><strong>Usage info:</strong> task history, ratings, streak/badge data, referrals.</li>
            <li><strong>Device info:</strong> IP address, browser type, basic device info (used for security and fraud prevention).</li>
          </ul>
        </Section>

        <Section title="How we use your information">
          <ul className="list-disc pl-6 space-y-1">
            <li>To run the marketplace — match posters with taskers, process payments and payouts, send notifications.</li>
            <li>To maintain leaderboards and streaks within your selected city.</li>
            <li>To enforce community safety — investigate reports, ban bad actors, prevent fraud.</li>
            <li>To improve the service — analyze aggregated usage patterns.</li>
            <li>To send essential service emails (account changes, payment receipts). We do not sell your data to advertisers.</li>
          </ul>
        </Section>

        <Section title="Third-party services we use">
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Stripe</strong> — payments, refunds, and tasker payouts. (<a className="underline" href="https://stripe.com/privacy" target="_blank" rel="noreferrer">policy</a>)</li>
            <li><strong>MongoDB Atlas</strong> — database hosting in the United States.</li>
            <li><strong>Google Fonts &amp; Fontshare</strong> — fonts loaded from CDNs (no tracking).</li>
          </ul>
        </Section>

        <Section title="Your rights">
          <p>You can:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>View and update your profile from your account settings.</li>
            <li>Delete your account at any time by emailing support@mycashstreak.com — we will erase your personal data within 30 days, except records we are legally required to keep (tax, fraud, dispute records via Stripe).</li>
            <li>Withdraw earnings to your bank via Stripe Connect.</li>
            <li>Request a copy of the data we hold about you.</li>
          </ul>
        </Section>

        <Section title="Children">
          <p>CashStreak is not intended for users under 18. We do not knowingly collect data from minors. If we learn a minor has signed up, we will delete the account.</p>
        </Section>

        <Section title="Security">
          <p>Passwords are hashed with bcrypt. Payment data flows directly to Stripe over TLS — we never see full card numbers. All traffic between your device and our servers is encrypted (HTTPS).</p>
        </Section>

        <Section title="Changes to this policy">
          <p>We may update this policy as the service evolves. Material changes will be announced in-app and via email at least 7 days before they take effect.</p>
        </Section>

        <Section title="Contact">
          <p>Questions, deletion requests, or concerns? Email <a className="underline font-bold" href="mailto:support@mycashstreak.com">support@mycashstreak.com</a>.</p>
        </Section>

        <div className="mt-12 brut-card p-5 bg-[var(--cs-yellow)]">
          <p className="text-sm font-semibold">By using CashStreak, you agree to this Privacy Policy and our <Link to="/terms" className="underline font-bold">Terms of Service</Link>.</p>
        </div>
      </main>
      <footer className="py-8 text-center text-xs text-[var(--cs-ink-2)]">© {new Date().getFullYear()} CashStreak</footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="font-display text-2xl font-extrabold tracking-tight">{title}</h2>
      <div className="mt-3 text-[var(--cs-ink)] leading-relaxed">{children}</div>
    </section>
  );
}
